import React from 'react';
import { Form } from 'antd';
import { useStore } from 'routes/inventorize_meter/useStore';
import { booleanToSelectValue } from 'utils/booleanToSelectValue';
import { WaterDetails } from 'components/Form/meter_details/water/WaterDetails';
import { WaterMeterDetailField } from 'components/Form/meter_details/water/constants';
import { FormattedMessage, useIntl } from 'react-intl';
import { Nullable } from '@enmon/commons';
import { MeterDetailWater } from '@enmon/commons/lib/enmon';
import { Merge } from 'type-fest';
import { FormItem, FormItemProps } from '../../../../../components/Form/FormItem';
import { validateFlowrate } from '../utils/validateFlowrate';
import { WaterAdditionalFields as messages } from '../messages';
import { BinarySelect } from '../../../../../components/Select/meter_details/Binary';

export type MeterWaterAdditionalDetails = Merge<
  MeterDetailWater,
  {
    flowrate?: {
      value: Nullable<number>;
      unit: Nullable<string>;
    } | null;
    hotWater?: number | null;
    woltman?: number | null;
    flange?: number | null;
  }
>;

export type MeterWaterDetailsProps = MeterWaterAdditionalDetails & {
  onFormItemReset?: FormItemProps['onResetField'];
  meterId?: number;
};

/* eslint-disable camelcase */
export const WaterAdditionalFields: React.VFC<MeterWaterDetailsProps> = props => {
  const { meterId, onFormItemReset, ...restTypeDetails } = props;
  const intl = useIntl();

  const {
    diameterNominal,
    flange,
    flowrate,
    hotWater,
    layLength,
    measurementType,
    orientation,
    tubeMaterial,
    woltman,
    secondSerialNumber,
  } = restTypeDetails;

  const form = Form.useFormInstance();

  const flowrateUnit = Form.useWatch<Nullable<string>>(['detail', 'flowrate', 'unit'], form) ?? flowrate?.unit;
  const flowrateValue = Form.useWatch<Nullable<number>>(['detail', 'flowrate', 'value'], form) ?? flowrate?.value;

  const { setDetailStepValues, twinMeter, newMeter } = useStore();

  // Has to be done in this way because of the typecasting in parent component
  const handleFlowrateReset = () => {
    const emptyFlowrateValue = {
      detail: { ...restTypeDetails, flowrate: { value: null, unit: null } } as MeterWaterAdditionalDetails,
    };

    form.setFieldsValue(emptyFlowrateValue);

    setDetailStepValues(emptyFlowrateValue);
  };

  return (
    <>
      {!newMeter && (
        <FormItem
          initialValue={secondSerialNumber ? 1 : twinMeter}
          label={<FormattedMessage {...messages.twinMeter} />}
          name="twinMeter"
        >
          <BinarySelect name="twin_meter" />
        </FormItem>
      )}

      <WaterDetails
        itemsConfig={{
          ...(!newMeter &&
            (!!twinMeter || !!secondSerialNumber) && {
              [WaterMeterDetailField.SecondSerialNumber]: {
                formItemProps: {
                  initialValue: secondSerialNumber,
                  isSemiRequired: !meterId,
                  onResetField: onFormItemReset,
                },
              },
            }),
          [WaterMeterDetailField.MeasurementType]: {
            formItemProps: {
              initialValue: measurementType,
              isSemiRequired: !meterId,
              onResetField: onFormItemReset,
            },
          },
          [WaterMeterDetailField.HotWater]: {
            formItemProps: {
              initialValue: booleanToSelectValue(hotWater),
              isSemiRequired: !meterId,
              onResetField: onFormItemReset,
            },
          },
          [WaterMeterDetailField.Flange]: {
            formItemProps: {
              initialValue: booleanToSelectValue(flange),
              isSemiRequired: !meterId,
              onResetField: onFormItemReset,
            },
          },
          ...(!newMeter && {
            [WaterMeterDetailField.Woltman]: {
              formItemProps: {
                initialValue: booleanToSelectValue(woltman),
                isSemiRequired: !meterId,
                onResetField: onFormItemReset,
              },
            },
          }),
          [WaterMeterDetailField.Orientation]: {
            formItemProps: { initialValue: orientation, isSemiRequired: !meterId, onResetField: onFormItemReset },
          },
          ...(!newMeter && {
            [WaterMeterDetailField.Flowrate]: {
              formItemProps: {
                isSemiRequired: !meterId,
                // Custom validation is used only in create mode
                rules: !meterId
                  ? [
                      {
                        required: !meterId,
                        message: intl.formatMessage(messages.flowRateRequired),
                      },
                      validateFlowrate,
                    ]
                  : [],
                onResetField: handleFlowrateReset,
              },
              additionalConfig: { flowrateUnit, flowrateValue },
            },
          }),
          [WaterMeterDetailField.LayLength]: {
            formItemProps: { initialValue: layLength, isSemiRequired: !meterId, onResetField: onFormItemReset },
          },
          [WaterMeterDetailField.DiameterNominal]: {
            formItemProps: { initialValue: diameterNominal, isSemiRequired: !meterId, onResetField: onFormItemReset },
          },
          [WaterMeterDetailField.TubeMaterial]: {
            formItemProps: {
              initialValue: tubeMaterial,
              isSemiRequired: !meterId,
              onResetField: onFormItemReset,
            },
          },
        }}
      />
    </>
  );
};
